import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import TableSection from '../../components/tables/TableSection';
import DeleteModal from '../../components/modals/DeleteModal';
import scheduledNotificationApi from '../../api/scheduled-notification-api';
import dayjs from 'dayjs';
import Spinner from '../../components/elements/Spinner/Spinner';

const DAYS_MAP = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];

const formatSchedule = (item) => {
  if (item.scheduleType === 'once') {
    return item.scheduledAt ? dayjs(item.scheduledAt).format('DD.MM.YYYY. HH:mm') : '-';
  }
  const time = `${String(item.hour ?? 0).padStart(2, '0')}:${String(item.minute ?? 0).padStart(2, '0')}`;
  const days =
    item.daysOfWeek && item.daysOfWeek.length > 0
      ? item.daysOfWeek.map((d) => DAYS_MAP[d]).join(', ')
      : 'Svaki dan';
  return `${time} — ${days}`;
};

const columns = [
  {
    key: 'notifTitle',
    label: 'Naslov',
    render: (_, row) => row.title?.sr || '-',
  },
  {
    key: 'scheduleType',
    label: 'Tip',
    render: (val) => (
      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, backgroundColor: val === 'once' ? '#f0e6ff' : '#e6f0ff', color: val === 'once' ? '#6d28d9' : '#1d5cbf' }}>
        {val === 'once' ? 'Jednokratno' : 'Ponavljajuće'}
      </span>
    ),
  },
  {
    key: 'schedule',
    label: 'Raspored',
    render: (_, row) => formatSchedule(row),
  },
  {
    key: 'status',
    label: 'Status',
    render: (val) => {
      const map = {
        active:   { bg: '#e6f4ea', color: '#1e7e34', label: 'Aktivno' },
        inactive: { bg: '#fdecea', color: '#c0392b', label: 'Neaktivno' },
        pending:  { bg: '#fff7e6', color: '#d46b08', label: 'Na čekanju' },
        sent:     { bg: '#e6f0ff', color: '#1d5cbf', label: 'Poslato' },
      };
      const s = map[val] ?? { bg: '#f0f0f0', color: '#555', label: val };
      return (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, backgroundColor: s.bg, color: s.color }}>
          {s.label}
        </span>
      );
    },
  },
  {
    key: 'lastDispatchedAt',
    label: 'Poslednje slanje',
    render: (val) => (val ? dayjs(val).format('DD.MM.YYYY. HH:mm') : '-'),
  },
];

export default function NotifikacijePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const initialPage = parseInt(queryParams.get('page')) || 1;

  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingToggleId, setLoadingToggleId] = useState(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await scheduledNotificationApi.getAll(currentPage, 10);
    if (success) {
      setNotifications(data.items);
      setTotalPages(data.totalPages);
    } else {
      toast.error(message || 'Greška pri učitavanju notifikacija');
    }
    setLoading(false);
  }, [currentPage]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = new URLSearchParams(location.search);
    params.set('page', page);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleToggle = async (item) => {
    setLoadingToggleId(item._id);
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    const { success, message } = await scheduledNotificationApi.toggleActive(item._id, newStatus);
    if (success) {
      toast.success(newStatus === 'active' ? 'Notifikacija aktivirana' : 'Notifikacija deaktivirana');
      loadNotifications();
    } else {
      toast.error(message || 'Greška pri promeni statusa');
    }
    setLoadingToggleId(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLoadingDelete(true);
    toast.loading('Brisanje notifikacije...');
    const { success, message } = await scheduledNotificationApi.remove(deleteTarget._id);
    toast.dismiss();
    if (success) {
      toast.success('Notifikacija obrisana');
      setDeleteTarget(null);
      loadNotifications();
    } else {
      toast.error(message || 'Greška pri brisanju');
    }
    setLoadingDelete(false);
  };

  const actions = [
    {
      key: 'header',
      label: 'Opcije',
      disabled: true,
      className: 'dropdown-header',
    },
    {
      key: 'detail',
      label: 'Detalji',
      icon: <img src="/assets/icons/view-icon.svg" alt="Detalji" />,
      onClick: (item) => navigate(`/notifikacije/${item._id}`, { state: { fromPage: currentPage } }),
    },
    {
      key: 'edit',
      label: 'Izmeni',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
      onClick: (item) => navigate(`/notifikacije/izmeni/${item._id}`, { state: { fromPage: currentPage } }),
    },
    {
      key: 'toggle',
      label: (item) => (item.status === 'active' ? 'Deaktiviraj' : 'Aktiviraj'),
      icon: <img src="/assets/icons/verification.svg" alt="Toggle" />,
      onClick: (item) => handleToggle(item),
      disabled: (item) => loadingToggleId === item._id,
      visible: (item) => item.scheduleType === 'recurring',
    },
    {
      key: 'delete',
      label: 'Obriši',
      icon: <img src="/assets/icons/delete.svg" alt="Obriši" />,
      danger: true,
      className: 'delete',
      onClick: (item) => setDeleteTarget(item),
    },
  ];

  return (
    <>
      <ContentLayout>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 className="title">Notifikacije</h2>
          <Button className="btn-primary" type="primary" onClick={() => navigate('/notifikacije/dodaj')}>
            Dodaj notifikaciju
          </Button>
        </div>

        {loading ? (
          <Spinner size="lg" />
        ) : (
          <TableSection
            columns={columns}
            actions={actions}
            data={notifications}
            emptyState={{
              icon: '/assets/icons/no-users.svg',
              title: 'Nema zakazanih notifikacija',
              description: 'Kreirajte prvu notifikaciju',
            }}
            showPagination={notifications.length > 0}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onSearch={() => {}}
            searchPlaceholder="Pretraži"
          />
        )}
      </ContentLayout>

      <DeleteModal
        visible={!!deleteTarget}
        onDelete={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={loadingDelete}
        title="Brisanje notifikacije"
        message={`Da li ste sigurni da želite da obrišete notifikaciju "${deleteTarget?.title?.sr}"?`}
        buttonText="Obriši notifikaciju"
        cancelText="Odustani"
      />
    </>
  );
}
